/**
 * Farbräder und Weissabgleich (S14).
 *
 * Sascha: „Drei Bereiche (Tiefen, Mitten, Höhen) statt eines Reglers, und ein
 * Klick auf etwas Graues richtet die Farben aus.“
 *
 * Die neun Zahlen sind die von `colorbalance`. Was hier steht, ist der Weg von
 * dem, was man **bedienen** kann, zu ihnen: zwei Schieber je Bereich und die
 * Pipette.
 */
/**
 * Zwischen -1 und 1, damit nichts durchrutscht, was ffmpeg ablehnt. Und
 * `-0` wird zu `0`: das ist dieselbe Zahl, sieht in einer Prüfung aber anders
 * aus und stand schon einmal in einer Meldung, die niemand deuten konnte.
 */
const klemm = (x) => {
    const y = Math.max(-1, Math.min(1, x));
    return y === 0 ? 0 : y;
};
/**
 * Wie viele Stufen (0..255) eine ganze Einheit verschiebt, über alle drei
 * Bereiche zugleich angewendet.
 *
 * Gemessen am 15. September an einer Farbfläche 200/160/120: mit ±0,2 wurde
 * daraus 162/159/156, also rund 190 Stufen je Einheit. Damals war das eine
 * Anpassung an `colorbalance`, das in seinen Bändern nicht geradlinig ist.
 *
 * **Seit dem 22. September ist es keine Anpassung mehr, sondern die Aussage
 * selbst** (7.146): der Export rechnet dieselbe Kennlinie wie die Vorschau,
 * und weil die drei Gewichte an jeder Stelle zusammen 1 ergeben, verschiebt
 * ein Wert auf allen drei Bereichen das Bild um **genau** so viele Stufen.
 */
export const STUFEN_JE_EINHEIT = 190;
/**
 * Die Zahlen, die eine gemessene Farbe neutral machen.
 *
 * Gerechnet wird gegen den Mittelwert der drei Kanäle und nicht gegen Weiss:
 * getippt wird auf etwas *Graues*, und wie hell das ist, soll sich nicht
 * ändern -- sonst würde aus einem Klick auf einen Schatten ein heller Fleck.
 */
export function weissabgleich(r, g, b) {
    const mittel = (r + g + b) / 3;
    return [klemm((mittel - r) / STUFEN_JE_EINHEIT), klemm((mittel - g) / STUFEN_JE_EINHEIT), klemm((mittel - b) / STUFEN_JE_EINHEIT)];
}
/**
 * Zwei Schieber statt eines Rades.
 *
 * `waerme` von -1 (kühl) bis 1 (warm) und `gruenMagenta` von -1 (grün) bis 1
 * (magenta). Ein Rad hat dieselben zwei Achsen -- es ist nur mit dem Daumen
 * nicht zu treffen.
 */
export function ausSchiebern(waerme, gruenMagenta) {
    // Warm heisst: mehr Rot, weniger Blau. Magenta: weniger Grün, und damit die
    // Helligkeit ungefähr bleibt, je zur Hälfte auf Rot und Blau.
    const w = klemm(waerme);
    const m = klemm(gruenMagenta);
    return [klemm(w + m / 2), klemm(-m), klemm(-w + m / 2)];
}
/** Und zurück, damit die Schieber dort stehen, wo die Zahlen sie hinsetzen. */
export function zuSchiebern([r, g, b]) {
    return { waerme: klemm((r - b) / 2), gruenMagenta: klemm(-g) };
}
/**
 * Die Stützstellen, mit denen die **Vorschau** die drei Bereiche nachzeichnet.
 *
 * `colorbalance` gibt es im Browser nicht. Was es gibt, ist eine Kennlinie je
 * Kanal (`feComponentTransfer type="table"`), und die kann dasselbe, solange
 * man ihr sagt, welcher Bereich wo liegt: Tiefen unten, Mitten in der Mitte,
 * Höhen oben, dazwischen geradlinig überblendet.
 *
 * Die drei Gewichte ergeben an **jeder** Stelle zusammen 1. Das ist kein
 * Zufall, sondern der Grund, warum man dieser Kurve trauen kann: steht auf
 * allen drei Bereichen derselbe Wert, verschiebt sie das ganze Bild um genau
 * `STUFEN_JE_EINHEIT` Stufen -- und das ist der Fall, der am 15. September an
 * echtem Material gemessen wurde. Einzeln gestellte Bereiche sind eine
 * Näherung dazwischen; gerechnet wird beim Export.
 */
export function farbrampe(tiefen, mitten, hoehen, stellen = 5) {
    const skala = STUFEN_JE_EINHEIT / 255;
    const raus = [];
    for (let i = 0; i < stellen; i++) {
        const t = i / (stellen - 1);
        const unten = Math.max(0, 1 - 2 * t);
        const mitte = 1 - Math.abs(2 * t - 1);
        const oben = Math.max(0, 2 * t - 1);
        const versatz = (unten * tiefen + mitte * mitten + oben * hoehen) * skala;
        raus.push(Math.max(0, Math.min(1, t + versatz)));
    }
    return raus;
}
//# sourceMappingURL=farbraeder.js.map